import React from 'react';
import { Heart, Users, Award, Target } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Patient-Centered Care',
      description: 'We prioritize the well-being and satisfaction of patients in all our staffing solutions.',
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Our network consists of highly qualified healthcare professionals with diverse specializations.',
    },
    {
      icon: Award,
      title: 'Quality Excellence',
      description: 'We maintain the highest standards in healthcare staffing through rigorous vetting processes.',
    },
    {
      icon: Target,
      title: 'Innovation',
      description: 'We leverage cutting-edge technology to streamline healthcare staffing solutions.',
    },
  ];

  return (
    <div className="about-page">
      <PageMeta
        title="About Us"
        description="Learn about BluekraftMed's mission, values, and commitment to excellence in healthcare staffing and patient care."
        path="/about"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">About BluekraftMed</h1>
            <p className="text-xl leading-relaxed">
              Leading Provider of Healthcare staffing services dedicated to excellence in patient care
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Our Mission</h2>
              <p className="text-gray-600 text-lg mb-4 leading-relaxed">
                Everything you do revolves around people. We assist you in developing, acquiring, and onboarding personnel in order to provide comprehensive patient care in exciting ways.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                Physicians, scientists and other medical experts dedicate a portion of their clinical time to this site, we are in the unique position to give you access to the knowledge and experience of Medical Physicians, scientists and other medical experts.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1638202993928-7267aad84c31?w=800&h=600&fit=crop"
                alt="Healthcare Team"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Core Values</h2>
            <p className="text-gray-600 text-lg">What makes us different and better</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                    <value.icon className="w-8 h-8 text-cyan-600" />
                  </div>
                  <CardTitle>{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=800&h=600&fit=crop"
                alt="Medical Experience"
                className="rounded-lg shadow-xl"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl font-bold mb-6">Experience and Reach</h2>
              <p className="text-xl text-gray-600 mb-4">The medical reach experience with knowledge</p>
              <p className="text-gray-600 text-lg leading-relaxed mb-4">
                Physicians, scientists and other medical experts dedicate a portion of their clinical time to this site, we are in the unique position to give you access to the knowledge and experience of Medical Physicians, scientists and other medical experts dedicate a portion of their clinical time to this site.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="text-center p-6 bg-cyan-50 rounded-lg">
                  <div className="text-4xl font-bold text-cyan-600 mb-2">99+</div>
                  <div className="text-gray-600">Expert Personals</div>
                </div>
                <div className="text-center p-6 bg-cyan-50 rounded-lg">
                  <div className="text-4xl font-bold text-cyan-600 mb-2">37+</div>
                  <div className="text-gray-600">Years of Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8">Why Choose BluekraftMed?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Innovative Culture',
                'High-Quality Patient Care',
                'Skilled Professionals',
                'Dedicated Staff',
                'Optimizing Delivery',
                'Customer Centricity',
                'Continuous Improvement',
                'Forward Thinking',
              ].map((item, index) => (
                <div key={index} className="bg-white bg-opacity-10 p-6 rounded-lg backdrop-blur-sm">
                  <p className="text-lg font-semibold">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;